import { F0Alert, F0Dialog, F0Text } from '@factorialco/f0-react'
import { Stack } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'

import { BlockingIssueItem } from 'modules/integrations/components/SyncButton/Modals/SyncReviewModal/BlockingIssueItem'
import { BlockingIssue } from 'modules/integrations/hooks/SyncContext/blockingIssues'

import styles from './SyncReviewModal.module.css'

type SyncReviewModalProps = {
  onClose: () => void
  onSubmit?: () => void
  blockingIssues: BlockingIssue[]
  anyValidSyncable: boolean
  hasFilters?: boolean
  isExpenses?: boolean
  title: string
  description?: string
  submitLabel?: string
}

export function SyncReviewModal({
  onClose,
  onSubmit,
  blockingIssues,
  anyValidSyncable,
  hasFilters = false,
  isExpenses = false,
  title,
  description,
  submitLabel,
}: SyncReviewModalProps) {
  const hasIssues = blockingIssues.length > 0

  const getDialogActions = () => {
    // No valid syncables → nothing to sync, only allow close
    if (!anyValidSyncable) {
      return {
        primaryAction: undefined,
        secondaryAction: { label: i18n.t('common.close'), onClick: onClose },
      }
    }

    // No issues → allow sync/export
    if (!hasIssues) {
      return {
        primaryAction: onSubmit
          ? { label: submitLabel ?? i18n.t('common.confirm'), onClick: onSubmit }
          : undefined,
        secondaryAction: { label: i18n.t('common.cancel'), onClick: onClose },
      }
    }

    // Has issues but some valid syncables → allow sync as secondary action (only if onSubmit provided)
    if (!onSubmit) {
      return {
        primaryAction: undefined,
        secondaryAction: { label: i18n.t('common.close'), onClick: onClose },
      }
    }

    return {
      primaryAction: undefined,
      secondaryAction: { label: submitLabel ?? i18n.t('common.confirm'), onClick: onSubmit },
    }
  }

  const { primaryAction, secondaryAction } = getDialogActions()


  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      width="sm"
      title={title}
      description={hasIssues ? description : undefined}
      primaryAction={primaryAction}
      secondaryAction={secondaryAction}
    >
      <Stack gap="4">
        {hasIssues ? (
          <Stack gap="4" className={styles.issuesList}>
            {blockingIssues.map((issue, index) => (
              <BlockingIssueItem key={index} blockingIssue={issue} />
            ))}
          </Stack>
        ) : description ? (
          <F0Text content={description} />
        ) : null}
        {(!hasIssues || anyValidSyncable) && hasFilters && (
          <F0Alert
            variant="info"
            title={i18n.t('integrations.confirmation_modal.filters_callout.title')}
            description={i18n.t('integrations.confirmation_modal.filters_callout.description')}
          />
        )}
        {isExpenses && !hasIssues && (
          <F0Alert
            variant="info"
            title={i18n.t('integrations.confirmation_modal.expenses_callout.title')}
            description={i18n.t('integrations.confirmation_modal.expenses_callout.description')}
          />
        )}
      </Stack>
    </F0Dialog>
  )
}
